/**
 * ValidationIssueCard Component
 *
 * Displays a single validation issue with severity-based styling and action buttons.
 * Supports suggestions, acknowledgment, edit, and remove actions.
 */

import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckIcon,
  PencilSquareIcon,
  TrashIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import type {
  ValidationIssue,
  EmployeeValidationResult,
  ValidationSeverity,
} from '@/types/validation';
import { getSeverityColor } from '@/types/validation';

// =============================================================================
// SINGLE ISSUE CARD
// =============================================================================

export interface SingleIssueCardProps {
  /** The validation issue to display */
  issue: ValidationIssue;
  /** Whether this warning has been acknowledged */
  isAcknowledged?: boolean;
  /** Callback when apply suggestion is clicked */
  onApplySuggestion?: (field: string, value: string) => void;
  /** Callback when acknowledge is clicked */
  onAcknowledge?: () => void;
}

/**
 * Displays a single validation issue.
 */
export function SingleIssueCard({
  issue,
  isAcknowledged = false,
  onApplySuggestion,
  onAcknowledge,
}: SingleIssueCardProps) {
  const colors = getSeverityColor(issue.severity);

  const getIcon = (severity: ValidationSeverity) => {
    switch (severity) {
      case 'ERROR':
        return <ExclamationCircleIcon className={`h-5 w-5 ${colors.icon}`} />;
      case 'WARNING':
        return <ExclamationTriangleIcon className={`h-5 w-5 ${colors.icon}`} />;
      case 'INFO':
        return <InformationCircleIcon className={`h-5 w-5 ${colors.icon}`} />;
    }
  };

  return (
    <div
      className={`p-3 rounded-lg border ${colors.border} ${colors.bg} ${
        isAcknowledged ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{getIcon(issue.severity)}</div>

        <div className="flex-1 min-w-0">
          {/* Issue message */}
          <p className={`text-sm ${colors.text}`}>
            {issue.field && (
              <span className="font-medium">{issue.field}: </span>
            )}
            {issue.message}
          </p>

          {/* Suggestion */}
          {issue.suggestion && (
            <div className="mt-2 flex items-center gap-2">
              <LightBulbIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
              <p className="text-xs text-gray-600">
                Did you mean:{' '}
                <span className="font-mono font-medium">
                  {issue.suggestion}
                </span>
              </p>
              {onApplySuggestion && issue.field && (
                <button
                  type="button"
                  onClick={() =>
                    onApplySuggestion(issue.field!, issue.suggestion!)
                  }
                  className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Apply
                </button>
              )}
            </div>
          )}

          {/* Acknowledgment button for warnings */}
          {issue.severity === 'WARNING' && onAcknowledge && !isAcknowledged && (
            <button
              type="button"
              onClick={onAcknowledge}
              className="mt-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 rounded hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <CheckIcon className="h-3.5 w-3.5" />
              Acknowledge
            </button>
          )}

          {/* Acknowledged indicator */}
          {issue.severity === 'WARNING' && isAcknowledged && (
            <span className="mt-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded">
              <CheckIcon className="h-3.5 w-3.5" />
              Acknowledged
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// EMPLOYEE ISSUE CARD
// =============================================================================

export interface ValidationIssueCardProps {
  /** Employee validation result */
  employeeResult: EmployeeValidationResult;
  /** Set of acknowledged warning codes for this employee */
  acknowledgedCodes?: Set<string>;
  /** Callback when apply suggestion is clicked */
  onApplySuggestion?: (
    employeeNi: string,
    field: string,
    value: string
  ) => void;
  /** Callback when edit employee is clicked */
  onEditEmployee?: (employeeNi: string) => void;
  /** Callback when remove employee is clicked */
  onRemoveEmployee?: (employeeNi: string) => void;
  /** Callback when acknowledge warning is clicked */
  onAcknowledgeWarning?: (employeeNi: string, code: string) => void;
  /** Callback when acknowledge all warnings for employee is clicked */
  onAcknowledgeAll?: (employeeNi: string) => void;
  /** Whether to show only errors or only warnings */
  showOnly?: 'errors' | 'warnings';
}

/**
 * Displays all validation issues for a single employee.
 */
export function ValidationIssueCard({
  employeeResult,
  acknowledgedCodes = new Set(),
  onApplySuggestion,
  onEditEmployee,
  onRemoveEmployee,
  onAcknowledgeWarning,
  onAcknowledgeAll,
  showOnly,
}: ValidationIssueCardProps) {
  const { employee_ni, employee_name, issues, row_number } = employeeResult;

  // Filter issues based on showOnly
  const displayIssues = issues.filter(issue => {
    if (showOnly === 'errors') return issue.severity === 'ERROR';
    if (showOnly === 'warnings') return issue.severity === 'WARNING';
    return issue.severity !== 'INFO'; // Default: show errors and warnings, not info
  });

  if (displayIssues.length === 0) return null;

  const hasErrors = displayIssues.some(i => i.severity === 'ERROR');
  const unacknowledgedWarnings = displayIssues.filter(
    i => i.severity === 'WARNING' && !acknowledgedCodes.has(i.code)
  );

  // Card border color based on most severe issue
  const borderColor = hasErrors ? 'border-red-200' : 'border-amber-200';
  const bgColor = hasErrors ? 'bg-red-50' : 'bg-amber-50';

  return (
    <div
      className={`rounded-lg border ${borderColor} ${bgColor} overflow-hidden`}
    >
      {/* Employee header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {employee_name || 'Unknown Employee'}
              </p>
              <p className="text-xs text-gray-500 font-mono">
                NI# {employee_ni}
                {row_number !== undefined && (
                  <span className="ml-2 text-gray-400">(Row {row_number})</span>
                )}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {/* Acknowledge all warnings button */}
            {unacknowledgedWarnings.length > 1 && onAcknowledgeAll && (
              <button
                type="button"
                onClick={() => onAcknowledgeAll(employee_ni)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 rounded hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <CheckIcon className="h-3.5 w-3.5" />
                Acknowledge All
              </button>
            )}

            {onEditEmployee && (
              <button
                type="button"
                onClick={() => onEditEmployee(employee_ni)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <PencilSquareIcon className="h-3.5 w-3.5" />
                Edit
              </button>
            )}

            {onRemoveEmployee && (
              <button
                type="button"
                onClick={() => onRemoveEmployee(employee_ni)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <TrashIcon className="h-3.5 w-3.5" />
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Issues list */}
      <div className="p-4 space-y-2">
        {displayIssues.map((issue, index) => (
          <SingleIssueCard
            key={`${issue.code}-${index}`}
            issue={issue}
            isAcknowledged={
              issue.severity === 'WARNING' && acknowledgedCodes.has(issue.code)
            }
            onApplySuggestion={
              onApplySuggestion
                ? (field, value) => onApplySuggestion(employee_ni, field, value)
                : undefined
            }
            onAcknowledge={
              issue.severity === 'WARNING' && onAcknowledgeWarning
                ? () => onAcknowledgeWarning(employee_ni, issue.code)
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

export default ValidationIssueCard;
